/**
 * Comparison Page — implements PRD §10.3.2 (COMP-07..18). Pivots an active
 * panel into a Shared / Unique / Hubs / Bridges view.
 *
 * Layout follows the screenshot mockups dated 2026-04-25:
 *   - Header: ← New search · Comparison · gene chips · summary · context
 *     filter · counts.
 *   - Persistent amber promiscuity caveat banner (COMP-09).
 *   - Tabs with counts (COMP-10), URL-hash-persisted via wouter's hash router.
 *   - Per-tab descriptive sub-banner.
 *   - Drilldown into the existing InteractionDetailDrawer (COMP-15) — we
 *     fetch the parent AnalysisResponse separately so the drawer has the
 *     full InteractionCandidate / PartnerProteinDetail context.
 */

import { useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileJson,
  FileSpreadsheet,
  GitCompare,
  Info,
  Loader2,
  ShieldAlert,
} from "lucide-react";

import {
  SPECIES,
  type BridgePartner,
  type ComparisonEdge,
  type ComparisonRequest,
  type ComparisonSet,
  type DecisionDto,
  type HubPartner,
  type InteractionCandidate,
  type SharedPartner,
  type SpeciesKey,
  type UniqueBlock,
} from "@shared/schema";
import { buildComparisonSet } from "@shared/comparison";

import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { EVIDENCE_TIER_META } from "@/lib/evidence";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { InteractionDetailDrawer } from "@/components/InteractionDetailDrawer";
import { cn } from "@/lib/utils";

type ComparisonTab = "shared" | "unique" | "hubs" | "bridges";

const TAB_DESCRIPTIONS: Record<ComparisonTab, string> = {
  shared:
    "Proteins that interact with multiple genes in your query set. Shared partners suggest functional convergence, co-complex membership, or pathway intersection.",
  unique:
    "Partners unique to a single query gene. High-priority unique partners may represent gene-specific functions not shared with other members of the panel.",
  hubs: "Hub proteins connect multiple genes in your query set with consistently high confidence. These may represent shared scaffolds, co-regulators, or members of a core complex. Note: very high-degree hub proteins may also be promiscuous binders — check risk flags per interaction.",
  bridges:
    "Bridge proteins connect two or more query genes via indirect interaction paths. These are candidates for pathway-level integration and may mediate crosstalk between your query proteins.",
};

interface ParsedQuery {
  genes: string[];
  species: SpeciesKey;
  tissue?: string;
  cellType?: string;
  diseaseMode?: string;
  repairState?: string;
}

function parseQuery(search: string): ParsedQuery {
  const params = new URLSearchParams(search);
  const genes =
    params
      .get("genes")
      ?.split(",")
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const species = (params.get("species") as SpeciesKey | null) ?? "human";
  return {
    genes,
    species,
    tissue: params.get("tissue") ?? undefined,
    cellType: params.get("cellType") ?? undefined,
    diseaseMode: params.get("diseaseMode") ?? undefined,
    repairState: params.get("repairState") ?? undefined,
  };
}

function buildContext(parsed: ParsedQuery, enabled: boolean): SidebarContext {
  // The cache key is shared with ResultsPage's analysisQ — both pages
  // ALWAYS pass a context object (with possibly-undefined fields when
  // `enabled` is false) so the keys serialise identically and TanStack
  // Query can dedupe + reuse the cached AnalysisResponse across pages.
  if (!enabled) {
    return { tissue: undefined, cellType: undefined, diseaseMode: undefined, repairState: undefined };
  }
  return {
    tissue: parsed.tissue,
    cellType: parsed.cellType,
    diseaseMode: parsed.diseaseMode,
    repairState: parsed.repairState,
  };
}

function buildRequest(parsed: ParsedQuery, contextEnabled: boolean): ComparisonRequest {
  const ctx = buildContext(parsed, contextEnabled);
  const allUndefined =
    ctx.tissue == null && ctx.cellType == null && ctx.diseaseMode == null && ctx.repairState == null;
  return {
    genes: parsed.genes,
    species: parsed.species,
    context: allUndefined ? undefined : ctx,
  };
}

type SidebarContext = {
  tissue?: string;
  cellType?: string;
  diseaseMode?: string;
  repairState?: string;
};

export function ComparisonPage() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const parsed = useMemo(() => parseQuery(search), [search]);
  const hasContext = !!(
    parsed.tissue ||
    parsed.cellType ||
    parsed.diseaseMode ||
    parsed.repairState
  );
  // COMP-09 / UX: if the user navigated here from a Results page that had
  // an active biological context, default to applying it. This both
  // (a) matches the user's mental model — they see the same data filtered
  // the same way — and (b) makes our analysis cache key match
  // ResultsPage's, which means we serve from cache instead of re-fetching.
  const [contextEnabled, setContextEnabled] = useState<boolean>(hasContext);
  const [tab, setTab] = useState<ComparisonTab>(() => readTabFromHash());
  const [selected, setSelected] = useState<InteractionCandidate | null>(null);

  const ctxObject = useMemo(
    () => buildContext(parsed, contextEnabled),
    [parsed, contextEnabled],
  );

  // ── Single source of truth: ONE analysis request, derived comparison ──
  //
  // We deliberately do NOT call /api/analysis/compare here. Two reasons:
  //   1. The server's compare endpoint internally calls runAnalysis() —
  //      so issuing both a `comparisonQ` and an `analysisQ` (needed for
  //      the drilldown drawer) would trigger TWO parallel runAnalysis
  //      executions, doubling the load on STRING/UniProt/AlphaFold and
  //      causing the rate-limit / abort storm we saw in the terminal log.
  //   2. `buildComparisonSet` is pure and now lives in `@shared/comparison`,
  //      so we can derive the set client-side from the cached
  //      AnalysisResponse with no round-trip.
  //
  // Using the same query key SHAPE as ResultsPage (`["analysis", genes,
  // species, contextObject]`) means TanStack Query reuses the in-memory
  // cache when the user clicks "Compare genes" → no fetch at all in the
  // common case.
  const analysisQ = useQuery({
    queryKey: ["analysis", parsed.genes, parsed.species, ctxObject],
    queryFn: () =>
      api.runAnalysis({
        genes: parsed.genes,
        species: parsed.species,
        context: ctxObject,
      }),
    enabled: parsed.genes.length >= 2,
  });

  const comparisonSet: ComparisonSet | undefined = useMemo(() => {
    if (!analysisQ.data) return undefined;
    try {
      return buildComparisonSet(analysisQ.data, parsed.genes);
    } catch (err) {
      // Defensive: the only throw path is < 2 query genes, which we already
      // gate on above. Surface the message so a regression is visible.
      console.error("buildComparisonSet failed", err);
      return undefined;
    }
  }, [analysisQ.data, parsed.genes]);

  const queryRunId = analysisQ.data?.queryRunId;
  const decisionsQ = useQuery({
    queryKey: ["run-decisions", queryRunId],
    queryFn: () => api.currentDecisionsForRun(getDeviceId(), queryRunId!),
    enabled: !!queryRunId,
  });

  const interactionsById = useMemo(() => {
    const map = new Map<string, InteractionCandidate>();
    for (const i of analysisQ.data?.interactions ?? []) map.set(i.id, i);
    return map;
  }, [analysisQ.data]);

  const openInteraction = (interactionId: string) => {
    const row = interactionsById.get(interactionId);
    if (row) setSelected(row);
  };

  const setTabAndHash = (next: ComparisonTab) => {
    setTab(next);
    writeTabToHash(next);
  };

  const downloadComparison = async (kind: "csv" | "json") => {
    const requestBody = buildRequest(parsed, contextEnabled);
    const url =
      kind === "csv"
        ? api.downloadComparisonCsvUrl()
        : api.downloadComparisonJsonUrl();
    const res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `academ-comparison-${comparisonSet?.queryRunId ?? "set"}.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  if (parsed.genes.length < 2) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <h1 className="font-display text-xl font-semibold">Comparison needs ≥ 2 genes</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Open a panel with at least two query genes and use the <em>Compare genes</em> button in the
          sidebar.
        </p>
        <Button variant="ghost" onClick={() => setLocation("/")} className="mt-3">
          <ArrowLeft className="h-4 w-4" /> Back to search
        </Button>
      </div>
    );
  }

  const summary = comparisonSet?.summary;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-4">
      <ComparisonHeader
        parsed={parsed}
        hasContext={hasContext}
        contextEnabled={contextEnabled}
        onToggleContext={setContextEnabled}
        summary={summary}
        onNewSearch={() => setLocation("/")}
        onBackToResults={() =>
          setLocation(`/results?${new URLSearchParams({
            genes: parsed.genes.join(","),
            species: parsed.species,
            ...(parsed.tissue ? { tissue: parsed.tissue } : {}),
            ...(parsed.cellType ? { cellType: parsed.cellType } : {}),
            ...(parsed.diseaseMode ? { diseaseMode: parsed.diseaseMode } : {}),
            ...(parsed.repairState ? { repairState: parsed.repairState } : {}),
          }).toString()}`)
        }
        onExportCsv={() => downloadComparison("csv")}
        onExportJson={() => downloadComparison("json")}
      />

      <Tabs
        value={tab}
        onChange={setTabAndHash}
        counts={{
          shared: summary?.sharedCount ?? 0,
          unique: summary?.uniqueCount ?? 0,
          hubs: summary?.hubsCount ?? 0,
          bridges: summary?.bridgesCount ?? 0,
        }}
      />

      <CaveatBanner />

      <p className="mt-3 text-[12px] leading-snug text-muted-foreground">
        {TAB_DESCRIPTIONS[tab]}
      </p>

      {analysisQ.isPending && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Resolving external sources for comparison…
        </div>
      )}

      {analysisQ.isError && (
        <div className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="font-semibold text-destructive">Comparison failed</p>
          <p className="mt-1 text-sm">{(analysisQ.error as Error)?.message ?? "Unknown error"}</p>
          <Button onClick={() => analysisQ.refetch()} className="mt-3">
            Retry
          </Button>
        </div>
      )}

      {comparisonSet && (
        <div className="mt-3 space-y-3">
          {tab === "shared" && (
            <SharedTab
              shared={comparisonSet.shared}
              decisions={decisionsQ.data ?? undefined}
              onOpen={openInteraction}
            />
          )}
          {tab === "unique" && (
            <UniqueTab
              unique={comparisonSet.unique}
              decisions={decisionsQ.data ?? undefined}
              onOpen={openInteraction}
            />
          )}
          {tab === "hubs" && (
            <HubsTab
              hubs={comparisonSet.hubs}
              decisions={decisionsQ.data ?? undefined}
              onOpen={openInteraction}
            />
          )}
          {tab === "bridges" && (
            <BridgesTab
              bridges={comparisonSet.bridges}
              decisions={decisionsQ.data ?? undefined}
              onOpen={openInteraction}
            />
          )}
        </div>
      )}

      <InteractionDetailDrawer
        row={selected}
        proteinInfo={analysisQ.data?.proteinInfo ?? {}}
        partnerDetails={analysisQ.data?.partnerDetails ?? {}}
        queryRunId={queryRunId}
        existingDecision={selected ? decisionsQ.data?.[selected.id] : undefined}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/* ───────────────────────── Header strip ───────────────────────── */

function ComparisonHeader({
  parsed,
  hasContext,
  contextEnabled,
  onToggleContext,
  summary,
  onNewSearch,
  onBackToResults,
  onExportCsv,
  onExportJson,
}: {
  parsed: ParsedQuery;
  hasContext: boolean;
  contextEnabled: boolean;
  onToggleContext: (v: boolean) => void;
  summary?: ComparisonSet["summary"];
  onNewSearch: () => void;
  onBackToResults: () => void;
  onExportCsv: () => void;
  onExportJson: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border pb-3">
      <button
        type="button"
        onClick={onNewSearch}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" /> New search
      </button>
      <button
        type="button"
        onClick={onBackToResults}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <GitCompare className="h-3 w-3 text-accent" />
        <span className="font-semibold text-foreground">Comparison</span>
      </button>
      <div className="flex flex-wrap items-center gap-1.5">
        {parsed.genes.map((g) => (
          <span
            key={g}
            className="rounded-md border border-border bg-card px-2 py-0.5 font-mono text-[11px] text-foreground"
          >
            {g}
          </span>
        ))}
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <Tooltip
          content={
            hasContext
              ? "Apply the active panel's biological context (tissue / cell type / disease mode) to the comparison."
              : "No biological context was set for the source panel."
          }
        >
          <label
            className={cn(
              "inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs",
              !hasContext && "cursor-not-allowed opacity-60",
            )}
          >
            <input
              type="checkbox"
              checked={contextEnabled}
              onChange={(e) => onToggleContext(e.currentTarget.checked)}
              disabled={!hasContext}
              className="h-3 w-3 accent-accent"
            />
            {contextEnabled
              ? `Context: ${parsed.tissue ?? "—"}${parsed.diseaseMode ? ` / ${parsed.diseaseMode}` : ""}`
              : "No context filter"}
          </label>
        </Tooltip>
        {summary && (
          <span>
            <span className="font-semibold text-foreground">{summary.totalInteractions}</span>{" "}
            interactions ·{" "}
            <span className="font-semibold text-foreground">{summary.sharedCount}</span> shared ·{" "}
            <span className="font-semibold text-foreground">{summary.hubsCount}</span> hubs
          </span>
        )}
        <span className="text-[10px]">
          species {SPECIES[parsed.species].label} · taxID {SPECIES[parsed.species].taxId}
        </span>
        <Button variant="outline" className="h-7 px-2 text-xs" onClick={onExportCsv}>
          <FileSpreadsheet className="h-3.5 w-3.5" />
          CSV
        </Button>
        <Button variant="outline" className="h-7 px-2 text-xs" onClick={onExportJson}>
          <FileJson className="h-3.5 w-3.5" />
          JSON
        </Button>
      </div>
    </div>
  );
}

function Tabs({
  value,
  onChange,
  counts,
}: {
  value: ComparisonTab;
  onChange: (v: ComparisonTab) => void;
  counts: Record<ComparisonTab, number>;
}) {
  const TABS: Array<{ id: ComparisonTab; label: string }> = [
    { id: "shared", label: "Shared" },
    { id: "unique", label: "Unique" },
    { id: "hubs", label: "Hubs" },
    { id: "bridges", label: "Bridges" },
  ];
  return (
    <nav className="mt-3 flex items-center gap-1 border-b border-border/60 text-sm">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors",
            value === t.id
              ? "border-accent text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label}
          <span
            className={cn(
              "inline-flex h-5 items-center rounded-full px-1.5 text-[11px] font-semibold",
              value === t.id
                ? "bg-accent/20 text-accent-foreground/80"
                : "bg-muted text-muted-foreground",
            )}
          >
            {counts[t.id]}
          </span>
        </button>
      ))}
    </nav>
  );
}

function CaveatBanner() {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-[hsl(38_90%_60%/0.5)] bg-[hsl(44_94%_95%)] px-3 py-2 text-xs dark:bg-[hsl(38_42%_16%)]">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(28_80%_40%)]" />
      <p className="leading-snug">
        Comparison analysis derives from parallel STRING queries per gene. Shared and hub proteins may
        include non-specific binders — always verify risk flags and evidence channels per interaction.
      </p>
    </div>
  );
}

/* ───────────────────────── Shared tab ───────────────────────── */

function SharedTab({
  shared,
  decisions,
  onOpen,
}: {
  shared: SharedPartner[];
  decisions?: Record<string, DecisionDto>;
  onOpen: (interactionId: string) => void;
}) {
  if (shared.length === 0) return <EmptyState label="No shared partners at the active confidence floor." />;
  return (
    <div className="space-y-2">
      {shared.map((s) => (
        <article key={s.partnerGene} className="rounded-lg border border-border bg-card">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[13px] font-semibold text-foreground">{s.partnerGene}</span>
              <span className="flex flex-wrap gap-1">
                {s.queryGenes.map((g) => (
                  <span
                    key={g}
                    className="rounded-md border border-border bg-background px-1.5 py-0 font-mono text-[10px] text-foreground"
                  >
                    {g}
                  </span>
                ))}
              </span>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {s.nQueryGenes} query genes · avg {s.avgScore1000}
            </span>
          </header>
          <ul className="divide-y divide-border/60 text-[12px]">
            {s.edges.map((e) => (
              <EdgeRow key={e.interactionId} edge={e} decision={decisions?.[e.interactionId]} onOpen={onOpen} />
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

/* ───────────────────────── Unique tab ───────────────────────── */

function UniqueTab({
  unique,
  decisions,
  onOpen,
}: {
  unique: UniqueBlock[];
  decisions?: Record<string, DecisionDto>;
  onOpen: (interactionId: string) => void;
}) {
  const blocks = unique.filter((u) => u.count > 0);
  if (blocks.length === 0) return <EmptyState label="No gene-unique partners at the active confidence floor." />;
  return (
    <div className="space-y-3">
      {blocks.map((u) => (
        <article key={u.queryGene} className="rounded-lg border border-border bg-card">
          <header className="flex items-center justify-between border-b border-border/60 px-3 py-2 text-[12px]">
            <span className="font-mono font-semibold text-foreground">
              {u.queryGene} <span className="text-muted-foreground">— unique partners ({u.count})</span>
            </span>
          </header>
          <ul className="divide-y divide-border/60 text-[12px]">
            {u.edges.map((e) => (
              <EdgeRow key={e.interactionId} edge={e} decision={decisions?.[e.interactionId]} onOpen={onOpen} />
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

/* ───────────────────────── Hubs tab ───────────────────────── */

function HubsTab({
  hubs,
  decisions,
  onOpen,
}: {
  hubs: HubPartner[];
  decisions?: Record<string, DecisionDto>;
  onOpen: (interactionId: string) => void;
}) {
  if (hubs.length === 0) {
    return (
      <EmptyState label="No partners crossed the hub threshold for this panel — try lowering the confidence slider on the Results page or adding more query genes." />
    );
  }
  return (
    <div className="space-y-2">
      {hubs.map((h) => (
        <article key={h.partnerGene} className="rounded-lg border border-border bg-card">
          <header className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 text-[12px]">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-muted font-mono text-[11px] text-muted-foreground">
              {h.rank}
            </span>
            <span className="font-mono text-[13px] font-semibold text-foreground">{h.partnerGene}</span>
            <span className="text-muted-foreground">
              connects {h.nQueryGenes} query genes · avg score {h.avgScore1000}
            </span>
            {h.promiscuityRisk && (
              <Tooltip content="High-degree hub — may bind non-specifically. Verify evidence channels and biological context.">
                <span className="inline-flex items-center gap-1 rounded-md border border-[hsl(38_90%_60%/0.5)] bg-[hsl(44_94%_95%)] px-1.5 py-0.5 text-[10px] text-[hsl(28_80%_40%)] dark:bg-[hsl(38_42%_16%)]">
                  <ShieldAlert className="h-3 w-3" /> high degree
                </span>
              </Tooltip>
            )}
          </header>
          <ul className="divide-y divide-border/60 text-[12px]">
            {h.edges.map((e) => (
              <EdgeRow key={e.interactionId} edge={e} decision={decisions?.[e.interactionId]} onOpen={onOpen} />
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

/* ───────────────────────── Bridges tab ───────────────────────── */

function BridgesTab({
  bridges,
  decisions,
  onOpen,
}: {
  bridges: BridgePartner[];
  decisions?: Record<string, DecisionDto>;
  onOpen: (interactionId: string) => void;
}) {
  if (bridges.length === 0) {
    return (
      <EmptyState label="No bridge partners detected — every shared partner already connects query genes that interact directly above the active confidence floor." />
    );
  }
  return (
    <div className="space-y-2">
      {bridges.map((b) => (
        <article key={b.partnerGene} className="rounded-lg border border-border bg-card">
          <header className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 text-[12px]">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-muted font-mono text-[11px] text-muted-foreground">
              {b.rank}
            </span>
            <span className="font-mono text-[13px] font-semibold text-foreground">{b.partnerGene}</span>
            <span className="text-muted-foreground">
              bridges {formatBridgedPairs(b)} · avg score {b.avgScore1000}
            </span>
          </header>
          <ul className="divide-y divide-border/60 text-[12px]">
            {b.edges.map((e) => (
              <EdgeRow key={e.interactionId} edge={e} decision={decisions?.[e.interactionId]} onOpen={onOpen} />
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function formatBridgedPairs(b: BridgePartner): string {
  // Collapse a list of pairs into a chain when possible: [A,B][B,C] → "A ↔ B ↔ C".
  const flat = new Set<string>();
  for (const [a, c] of b.bridgedPairs) {
    flat.add(a);
    flat.add(c);
  }
  if (flat.size === b.queryGenes.length) {
    return b.queryGenes.join(" ↔ ");
  }
  return b.bridgedPairs.map(([a, c]) => `${a} ↔ ${c}`).join(", ");
}

/* ───────────────────────── Shared row + helpers ───────────────────────── */

function EdgeRow({
  edge,
  decision,
  onOpen,
}: {
  edge: ComparisonEdge;
  decision?: DecisionDto;
  onOpen: (interactionId: string) => void;
}) {
  const meta = EVIDENCE_TIER_META[edge.evidenceTier];
  const widthPct = Math.max(2, Math.round((edge.combinedScore1000 / 1000) * 100));
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(edge.interactionId)}
        className="flex w-full items-center gap-3 px-3 py-1.5 text-left transition-colors hover:bg-muted/40"
      >
        <span className="font-mono text-[12px] text-foreground">{edge.queryGene}</span>
        <span className="text-muted-foreground">→</span>
        <span className="font-mono text-[12px] font-semibold text-foreground">{edge.partnerGene}</span>
        <Badge
          className="ml-1 px-1.5 py-0 text-[10px]"
          style={{ backgroundColor: meta.color + "22", color: meta.color, borderColor: meta.color + "55" }}
        >
          {meta.label}
        </Badge>
        <span className="ml-auto flex w-32 items-center gap-1.5">
          <span className="h-1.5 flex-1 rounded-sm bg-muted">
            <span
              className="block h-full rounded-sm"
              style={{ width: `${widthPct}%`, backgroundColor: meta.color }}
            />
          </span>
          <span className="font-mono text-[11px] tabular-nums text-foreground">
            {edge.combinedScore1000}
          </span>
        </span>
        {decision && (
          <Tooltip content={`Decision: ${decision.action.replace("_", " ")}${decision.reason ? ` — ${decision.reason}` : ""}`}>
            <span
              className={cn(
                "priority-dot",
                decision.action === "validate_now" && "bg-priority-high",
                decision.action === "maybe_later" && "bg-priority-mid",
                decision.action === "reject" && "bg-destructive",
              )}
            />
          </Tooltip>
        )}
        <ExternalLink className="h-3 w-3 text-muted-foreground/60" aria-hidden />
      </button>
    </li>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{label}</p>
    </div>
  );
}

/* ───────────────────────── Tab persistence (COMP-10) ───────────────────────── */

const VALID_TABS: ComparisonTab[] = ["shared", "unique", "hubs", "bridges"];
const TAB_STORAGE_KEY = "academ.comparison.tab";

// History note: an earlier implementation persisted the active tab via
// `history.replaceState` to a `&t=…` suffix on the hash. That looked clean
// but had a fatal interaction with wouter's hash router: wouter stores the
// genes/species/context query in `location.search` (NOT inside the hash),
// and rewriting the URL via replaceState dropped that search portion —
// every tab click silently wiped the gene list, sending the page into the
// "Comparison needs ≥ 2 genes" empty state. The current sessionStorage
// approach achieves the same UX (tab survives reloads within a session)
// without ever touching the URL.

function readTabFromHash(): ComparisonTab {
  if (typeof window === "undefined") return "shared";
  try {
    const t = window.sessionStorage.getItem(TAB_STORAGE_KEY) as ComparisonTab | null;
    return t && VALID_TABS.includes(t) ? t : "shared";
  } catch {
    // sessionStorage can throw in privacy modes / sandboxed iframes — fall
    // back silently to the default tab.
    return "shared";
  }
}

function writeTabToHash(tab: ComparisonTab) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // ignore — best-effort persistence only
  }
}
